import clsx from 'clsx'
import { useMemo, useState } from 'react'
import { Menu, X } from 'lucide-react'
import { SearchBox } from '../cc'
import { REPORT_GROUPS } from './reportCatalog'
import { reportCard, reportSidebarActive } from './ui'

interface ReportSidebarProps {
  activeId: string
  onSelect: (id: string) => void
  available: Set<string>
  configured: boolean
  /** Persistent 240px desktop rail. False = menu button + overlay only (full page). */
  rail?: boolean
}

export function ReportSidebar({ activeId, onSelect, available, configured, rail = true }: ReportSidebarProps) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(REPORT_GROUPS.map((g) => g.label)))

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return REPORT_GROUPS
    return REPORT_GROUPS.map((g) => ({
      ...g,
      items: g.items.filter(
        (it) => it.label.toLowerCase().includes(q) || it.id.toLowerCase().includes(q),
      ),
    })).filter((g) => g.items.length > 0)
  }, [query])

  const totalCount = REPORT_GROUPS.reduce((n, g) => n + g.items.length, 0)

  const content = (
    <div className="flex h-full flex-col">
      <div className="shrink-0 border-b border-[rgba(15,23,42,0.07)] p-3">
        <SearchBox
          value={query}
          onChange={setQuery}
          placeholder="Search reports…"
          counter={`${totalCount}`}
        />
        {!configured ? (
          <p className="mt-2 rounded-[8px] border border-[rgba(244,63,94,0.26)] bg-[rgba(244,63,94,0.05)] px-2 py-1.5 text-[11px] text-[#9F1239]">
            Reporting database not configured
          </p>
        ) : null}
      </div>
      <nav className="min-h-0 flex-1 overflow-auto p-2">
        {filtered.map((g) => {
          const isExp = expanded.has(g.label)
          return (
            <div key={g.label} className="mb-1">
              <button
                type="button"
                onClick={() => setExpanded((prev) => {
                  const next = new Set(prev)
                  if (next.has(g.label)) next.delete(g.label)
                  else next.add(g.label)
                  return next
                })}
                className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 transition-colors duration-100 hover:bg-slate-50 hover:text-slate-700"
              >
                {g.label}
                <span className="font-mono text-xs font-normal text-slate-400">{g.items.length}</span>
              </button>
              {isExp ? (
                <ul className="mt-0.5 animate-fade-in space-y-0.5">
                  {g.items.map((it) => {
                    const disabled = !available.has(it.id)
                    const active = it.id === activeId
                    return (
                      <li key={it.id}>
                        <button
                          type="button"
                          disabled={disabled}
                          onClick={() => { onSelect(it.id); setOpen(false) }}
                          className={clsx(
                            'relative w-full rounded-md py-2 pl-3 pr-3 text-left text-sm transition-colors duration-100',
                            active && clsx(reportSidebarActive, 'font-semibold'),
                            !active && !disabled && 'text-slate-700 hover:bg-slate-50',
                            disabled && 'cursor-not-allowed opacity-40',
                          )}
                        >
                          <div>{it.label}</div>
                          {it.description ? (
                            <div className="mt-0.5 truncate text-xs text-slate-500">{it.description}</div>
                          ) : null}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              ) : null}
            </div>
          )
        })}
      </nav>
    </div>
  )

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={clsx(
          'flex h-8 items-center gap-2 rounded-lg bg-white px-3 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-gray-900/5',
          rail && 'lg:hidden',
        )}
      >
        <Menu className="h-4 w-4" />
        Reports
      </button>

      {rail ? (
        <aside className={clsx('hidden w-[240px] shrink-0 flex-col overflow-hidden lg:flex', reportCard)}>
          {content}
        </aside>
      ) : null}

      {open ? (
        <>
          <div
            className={clsx('fixed inset-0 z-40 animate-fade-in bg-[rgba(15,23,42,0.3)]', rail && 'lg:hidden')}
            onClick={() => setOpen(false)}
          />
          <aside
            className={clsx(
              'fixed bottom-0 left-0 top-0 z-50 flex w-[280px] animate-slide-up flex-col bg-white shadow-[0_10px_30px_-18px_rgba(15,23,42,0.55)]',
              rail && 'lg:hidden',
            )}
          >
            <div className="flex items-center justify-between border-b border-[rgba(15,23,42,0.07)] px-3 py-2">
              <span className="text-sm font-semibold text-slate-900">Reports</span>
              <button type="button" onClick={() => setOpen(false)} className="p-1 text-slate-500">
                <X className="h-5 w-5" />
              </button>
            </div>
            {content}
          </aside>
        </>
      ) : null}
    </>
  )
}
